import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../../utils/tpoGenQueryKeys';
import { InfeasibilityStatus } from '../../../../types/infeasiblePlan';
import type { InfeasibilityConstraintGroup } from '../../../../types/infeasiblePlan';

interface InfeasibilityExplanationResponse {
    job_id?: string;
    job_status?: string;
    data?: InfeasibilityConstraintGroup[] | null;
}

/** The backend answers with an empty payload while the diagnosis is still running. */
const readConstraintGroups = (
    response: InfeasibilityExplanationResponse | undefined,
): InfeasibilityConstraintGroup[] | null => {
    const groups = response?.data;
    return Array.isArray(groups) && groups.length > 0 ? groups : null;
};

const fetchInfeasibilityExplanation = async (
    jobId: string,
    signal?: AbortSignal,
): Promise<InfeasibilityExplanationResponse> => {
    const response = await axios(tpoGenUrls.getInfeasibilityExplanation(jobId), {
        method: 'GET',
        signal,
    });
    return response.data;
};

interface UseInfeasibilityPollingProps {
    jobId: string | null;
    enabled: boolean;
    pollingIntervalMs: number;
}

/**
 * Polls the infeasibility diagnosis for a job the solver declared infeasible.
 * The backend computes the relaxed-constraint report asynchronously, so it
 * answers with an empty payload until the diagnosis lands.
 */
const useInfeasibilityPolling = ({ jobId, enabled, pollingIntervalMs }: UseInfeasibilityPollingProps) => {
    const isEnabled = Boolean(jobId) && enabled;

    const { data, isError } = useQuery({
        queryKey: tpoGenKeys.infeasibilityExplanation(jobId!),
        queryFn: ({ signal }) => fetchInfeasibilityExplanation(jobId!, signal),
        enabled: isEnabled,
        refetchInterval: (query) => {
            // A failed diagnosis is terminal: keeping the interval alive would
            // retry a request the backend has already refused, indefinitely.
            if (query.state.status === 'error') return false;
            if (readConstraintGroups(query.state.data)) return false;
            return pollingIntervalMs;
        },
        refetchOnWindowFocus: false,
        staleTime: 0,
    });

    const constraints = readConstraintGroups(data);

    let infeasibilityStatus = InfeasibilityStatus.IDLE;
    if (isEnabled) {
        if (isError) {
            infeasibilityStatus = InfeasibilityStatus.ERROR;
        } else if (constraints) {
            infeasibilityStatus = InfeasibilityStatus.COMPLETED;
        } else {
            infeasibilityStatus = InfeasibilityStatus.POLLING;
        }
    }

    return {
        infeasibilityStatus,
        infeasibilityData: constraints,
    };
};

export default useInfeasibilityPolling;
